/**
 * 브랜드 상수 — 단일 브랜드(yoy.live).
 *
 * 이전에는 hostname 으로 enter.fun / aether-fun.xyz 를 런타임 분기했으나,
 * yoy.live 완전 대체 결정에 따라 분기를 제거했다
 * (docs/yoy-live-domain-migration.md §4.2, Phase 3).
 *
 * 구 도메인은 Phase 5(최소 30일 후)까지 라우트가 살아 있지만, 그 호스트로
 * 들어와도 **표기·API 모두 yoy.live 를 쓴다** — 그게 "완전 대체"의 의미다.
 * 구 오리진은 apps/api/src/lib/allowed-origins.ts 에 남아 있으므로 CORS 는 통과한다.
 *
 * 함수 시그니처는 의도적으로 그대로 두었다. 소비처가 20곳 이상이라
 * 내부만 상수화하면 호출부를 건드릴 필요가 없다(§4.2).
 */

// 상수 자체는 `@repo/shared-types/brand` 가 단일 소스다 — Workers 런타임
// (apps/stream-control/worker)에서도 import 해야 하므로 `window` 를 참조하는
// 이 파일에 둘 수 없다. 여기는 **호스트 분기 로직**만 담당한다.
import {
  APP_ZONES,
  BRAND_COMPANY_NAMES,
  BRAND_DOMAIN,
  BRAND_LABEL,
  PRODUCTION_ZONES,
  STAGING_ZONES,
} from '@repo/shared-types/brand';

const API_ORIGIN = `https://api.${BRAND_DOMAIN}`;

export function getBrandDomain(): string {
  return BRAND_DOMAIN;
}

export function getBrandLabel(): string {
  return BRAND_LABEL;
}

export function getBrandCompanyNames(): { ko: string; en: string } {
  return { ...BRAND_COMPANY_NAMES };
}

/**
 * API 베이스 URL.
 *
 * production 은 항상 `https://api.yoy.live`. staging/local 은 빌드타임
 * `VITE_API_URL`(= `fallback`)을 쓴다 — 그래서 hostname 을 여전히 본다.
 * staging 은 이 마이그레이션의 스코프 밖이므로 절대 yoy 로 보내면 안 된다.
 */
export function getApiUrl(fallback?: string): string {
  if (typeof window === 'undefined') return fallback ?? '';
  const host = window.location.hostname;
  return isProductionHost(host) ? API_ORIGIN : (fallback ?? '');
}

/**
 * production 존인지 판정.
 *
 * 구 두 존도 Phase 5 까지는 production 이다 — 그 호스트로 들어온 사용자도
 * 새 API 를 써야 하므로 여기 포함한다. staging(`dev-unopnd.xyz`)·localhost 는 제외.
 *
 * `host === d || host.endsWith('.' + d)` 형태여야 한다. `endsWith(d)` 로 쓰면
 * `notyoy.live` 가, prefix 매칭으로 쓰면 `yoy.live.evil.com` 이 통과한다.
 * 이 판정이 API 오리진을 정하므로 오판 시 토큰이 남의 호스트로 나간다.
 */
function isProductionHost(host: string): boolean {
  return PRODUCTION_ZONES.some((d) => host === d || host.endsWith(`.${d}`));
}

/**
 * 형제 앱 URL — 선두 호스트 라벨을 교체해 환경별 설정 없이 동작한다.
 * streamer.yoy.live → editor.yoy.live,
 * streamer.enter-dot-fun.dev-unopnd.xyz → editor.enter-dot-fun.dev-unopnd.xyz.
 *
 * apex(`yoy.live`)에서는 라벨을 **교체하지 않고 앞에 붙인다**. 교체하면
 * `yoy.live` → `editor.live` 라는 보유하지 않은 도메인이 나온다.
 * localhost/IP(dev) 에서는 `fallback` 을 반환한다.
 */
export function siblingAppUrl(subdomain: string, fallback?: string): string {
  if (typeof window === 'undefined') return fallback ?? '';
  const host = window.location.hostname;
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return fallback ?? '';

  // apex 판정은 **알려진 존 목록**으로 한다. 라벨 개수로 세면 안 된다 —
  // staging 존 `enter-dot-fun.dev-unopnd.xyz` 는 라벨이 3개인 apex 라서
  // "3개 이상이면 서브도메인이 있다"고 가정하면 선두 라벨을 지워
  // `editor.dev-unopnd.xyz`(보유하지 않은 도메인)를 만든다.
  //
  // 목록에 없는 2-label 호스트(신규 존 추가 등)는 서브도메인이 있을 수 없으므로
  // apex 로 함께 처리한다 — 이 폴백이 없으면 신규 존 apex 에서 같은 버그가 재발한다.
  const parts = host.split('.');
  const isApex = (APP_ZONES as readonly string[]).includes(host) || parts.length <= 2;
  if (isApex) return `https://${subdomain}.${host}`;

  parts[0] = subdomain;
  return `https://${parts.join('.')}`;
}

/** 쇼츠 에디터 앱 URL (dev → `fallback`). */
export function getEditorUrl(fallback?: string): string {
  return siblingAppUrl('editor', fallback);
}

/**
 * **초대 링크처럼 브랜드를 고정해야 하는** 앱 오리진.
 *
 * `siblingAppUrl` 과 다른 점: siblingAppUrl 은 *현재 존을 유지*한다
 * (`s.enter.fun` → `streamer.enter.fun`). 초대 링크는 그러면 안 된다 —
 * 구 도메인으로 접속한 스트리머가 만든 링크가 구 도메인을 가리키면 Phase 5 에서
 * 301 체인을 타고, 그 리다이렉트는 발급된 링크 수명보다 먼저 사라질 수 있다
 * (docs/yoy-live-domain-migration.md §4.2). 그래서 production 은 **항상 yoy.live**.
 *
 * staging 은 마이그레이션 스코프 밖이므로 자기 존을 유지한다.
 * localhost/IP 는 앱마다 포트가 다르므로 `fallback` 을 그대로 돌려준다 —
 * 여기서 `window.location.origin` 을 쓰면 안 된다. 로컬에서 streamer(:5175) 오리진에
 * `/invite/...` 를 붙이면 그 경로는 streamer 앱에 없어서 `*` → `/dashboard` 로 튕긴다.
 */
function brandFixedOrigin(subdomain: string, fallback?: string): string {
  const canonical = `https://${subdomain}.${BRAND_DOMAIN}`;
  // SSR/비브라우저 — 로컬 포트 fallback 을 쓰면 안 된다. 이 함수의 결과는 **공유되는
  // 링크**로 나가므로, 호스트를 모를 때의 안전한 답은 localhost 가 아니라 정규 도메인이다.
  if (typeof window === 'undefined') return canonical;

  const host = window.location.hostname;
  if (isLocalHostname(host)) return fallback ?? canonical;

  // `host === z || host.endsWith('.' + z)` 형태를 지킨다. `endsWith(z)` 로 쓰면
  // `notenter-dot-fun.dev-unopnd.xyz` 가 staging 으로 오판된다(brand.ts 상단 주석과 동일 함정).
  const staging = STAGING_ZONES.find((z) => host === z || host.endsWith(`.${z}`));
  if (staging) return `https://${subdomain}.${staging}`;

  // production(구 존 포함) + 알 수 없는 호스트 → 정규 브랜드로 고정.
  return canonical;
}

/**
 * 로컬 개발 호스트 판정. `localhost` 와 IPv4 리터럴만 보면 부족하다 —
 * `[::1]`, `*.localhost`, `*.local`(mDNS), LAN 호스트명으로 dev 서버를 띄우는 경우
 * 프로덕션 링크를 만들어 로컬에서 검증이 불가능해진다.
 */
function isLocalHostname(host: string): boolean {
  if (host === 'localhost' || host.endsWith('.localhost')) return true;
  if (host === '::1' || host === '[::1]') return true;
  if (host.endsWith('.local')) return true;
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
}

/**
 * 공개 초대/리다이렉트 랜딩 오리진 (`s.*`) — apps/stream-control 이 서빙한다.
 * 라우트 정의는 apps/stream-control/wrangler.jsonc.
 * @param fallback 로컬 dev 용. stream-control vite dev 포트는 5195.
 */
export function getShareLandingOrigin(fallback = 'http://localhost:5195'): string {
  return brandFixedOrigin('s', fallback);
}

/**
 * 스트리머 앱 오리진 (`streamer.*`) — 초대 랜딩의 "시작하기" 목적지.
 * @param fallback 로컬 dev 용. streamer vite dev 포트는 5175.
 */
export function getStreamerAppOrigin(fallback = 'http://localhost:5175'): string {
  return brandFixedOrigin('streamer', fallback);
}

/** 스트리머용 초대 공유 링크 전문. 코드가 없으면 빈 문자열. */
export function buildInviteShareUrl(referralCode: string | null | undefined): string {
  if (!referralCode) return '';
  return `${getShareLandingOrigin()}/invite/${encodeURIComponent(referralCode)}`;
}
